import useNoindex from "../lib/useNoindex";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { formatTag } from "../lib/format";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Slider } from "../components/ui/slider";
import { ArrowLeft, ExternalLink, Send, CheckCircle2, Lock } from "lucide-react";

export default function JudgeScore() {
  useNoindex();
  const { team_id } = useParams();
  const [searchParams] = useSearchParams();
  const roundParam = searchParams.get("round");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [scores, setScores] = useState({ innovation: 7, impact: 7, feasibility: 7, execution: 7, presentation: 7 });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = roundParam ? `?round=${roundParam}` : "";
      const { data } = await api.get(`/judge/team/${team_id}${q}`);
      setState(data);
    } catch (err) { toast.error(errorMessage(err)); navigate("/judge/dashboard"); }
  }, [team_id, roundParam, navigate]);
  useEffect(() => { if (user?.role === "judge") load(); }, [user, load]);

  const round = state?.round;
  const weighted = useMemo(() => {
    if (!round) return 0;
    if (round === 1) return scores.innovation*0.30 + scores.impact*0.30 + scores.feasibility*0.25 + scores.presentation*0.15;
    return scores.innovation*0.25 + scores.impact*0.25 + scores.feasibility*0.20 + scores.execution*0.20 + scores.presentation*0.10;
  }, [scores, round]);

  if (loading) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;
  if (!user || user.role !== "judge") return <Navigate to="/login" replace />;
  if (!state) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>;

  if (state.already_scored) {
    return (
      <div className="App" data-testid="judge-score-already">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16 text-center">
          <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-display text-3xl text-white">You've already scored this team</h1>
          <p className="text-gray-400 mt-3">Scores are locked once submitted to prevent bias.</p>
          <button onClick={() => navigate("/judge/dashboard")} className="btn-primary mt-6">Back to Dashboard</button>
        </div>
        <Footer />
      </div>
    );
  }

  const sub = state.submission;
  const locked = !!state.scoring_locked;
  const lockDate = state.judging_deadline ? new Date(state.judging_deadline).toLocaleString() : "";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/judge/score", {
        team_id, round,
        innovation_score: scores.innovation,
        impact_score: scores.impact,
        feasibility_score: scores.feasibility,
        execution_score: round === 2 ? scores.execution : 0,
        presentation_score: scores.presentation,
        internal_notes: notes || null,
      });
      toast.success("Score submitted");
      navigate("/judge/dashboard");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const criteria = round === 1 ? [
    { k: "innovation", l: "Innovation & Originality" },
    { k: "impact", l: "Problem Relevance & Real-world Impact" },
    { k: "feasibility", l: "Feasibility of Solution" },
    { k: "presentation", l: "Solution Clarity" },
  ] : [
    { k: "innovation", l: "Innovation & Originality" },
    { k: "impact", l: "Problem Relevance & Real-world Impact" },
    { k: "feasibility", l: "Feasibility of Solution" },
    { k: "execution", l: "Technical Execution" },
    { k: "presentation", l: "Presentation Quality" },
  ];

  return (
    <div className="App" data-testid="judge-score-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/judge/dashboard")} className="text-gray-400 hover:text-white text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="label-mono mb-2">Scoring · Round {round}</div>
        <h1 className="font-display text-4xl text-white">{state.team.name}</h1>
        {round === 1 && <p className="text-brand mt-1">{sub.project_title}</p>}

        {locked && (
          <div className="mt-6 card-surface border-l-2 border-amber-500 bg-amber-500/5" data-testid="judging-locked-banner">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-400" />
              <div>
                <div className="text-white font-medium">Judging closed{lockDate ? ` on ${lockDate}` : ""}.</div>
                <div className="text-sm text-gray-400">Scores are now locked. You can view this submission but can no longer score it.</div>
              </div>
            </div>
          </div>
        )}

        {/* Submission display */}
        <div className="mt-8 card-surface space-y-5" data-testid="submission-view">
          {round === 1 && (
            <>
              <Row label="Category">{sub.category}</Row>
              <Row label="Target Audience">{sub.target_audience}</Row>
              <Row label="Idea Stage">{formatTag(sub.idea_stage)}</Row>
              <Row label="Tech Stack">
                <div className="flex flex-wrap gap-2 mt-1">
                  {(sub.tech_stack || []).map(t => <span key={t} className="px-2 py-1 text-xs font-mono uppercase tracking-widest bg-white/5 border border-white/10 text-gray-300">{t}</span>)}
                </div>
              </Row>
              <Row label="Problem Statement"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.problem_statement}</div></Row>
              <Row label="Proposed Solution"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.proposed_solution}</div></Row>
              <Row label="Inspiration"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.inspiration}</div></Row>
              <a href={sub.pitch_video_link} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 !text-xs" data-testid="watch-video">
                <ExternalLink className="w-4 h-4" /> Watch Pitch Video →
              </a>
            </>
          )}
          {round === 2 && (
            <>
              {sub.problem_solved && <Row label="What problem does it solve"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.problem_solved}</div></Row>}
              {sub.prototype_status && <Row label="Prototype Status">{sub.prototype_status}</Row>}
              {(sub.tech_stack && sub.tech_stack.length > 0) && (
                <Row label="Tech Stack">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sub.tech_stack.map(t => <span key={t} className="px-2 py-1 text-xs font-mono uppercase tracking-widest bg-white/5 border border-white/10 text-gray-300">{t}</span>)}
                  </div>
                </Row>
              )}
              <Row label="What changed from Round 1"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.changes_from_r1}</div></Row>
              <Row label="Biggest Challenge"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.biggest_challenge}</div></Row>
              {sub.next_step_if_win && <Row label="Next step if they win"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.next_step_if_win}</div></Row>}
              {sub.build_next && <Row label="What they'd build next"><div className="text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{sub.build_next}</div></Row>}
              {Array.isArray(sub.team_roles) && sub.team_roles.length > 0 && (
                <Row label="Team Roles">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sub.team_roles.map((r, i) => <span key={i} className="px-2.5 py-1 text-xs font-mono bg-white/5 border border-white/10 text-gray-300"><span className="text-white">{r.name}</span> · {r.role}</span>)}
                  </div>
                </Row>
              )}
              <div className="flex flex-wrap gap-2 pt-3">
                <a href={sub.pitch_video_link} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 !text-xs" data-testid="watch-video"><ExternalLink className="w-4 h-4" /> Watch Pitch Video →</a>
                {sub.repo_link && <a href={sub.repo_link} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 !text-xs" data-testid="view-repo"><ExternalLink className="w-4 h-4" /> View Repository →</a>}
                {sub.demo_link && <a href={sub.demo_link} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 !text-xs" data-testid="view-demo"><ExternalLink className="w-4 h-4" /> View Demo →</a>}
              </div>
            </>
          )}
        </div>

        {/* Scoring */}
        {!locked && (
        <div className="mt-10 card-surface" data-testid="scoring-section">
          <div className="label-mono mb-5">Score · 1-10 each criterion</div>
          <div className="space-y-6">
            {criteria.map(c => (
              <div key={c.k} data-testid={`criterion-${c.k}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white">{c.l}</label>
                  <input type="number" min="1" max="10" step="0.5" value={scores[c.k]}
                    onChange={e => setScores(s => ({ ...s, [c.k]: parseFloat(e.target.value) || 1 }))}
                    className="input-field !w-20 !py-1 text-center font-mono"
                    data-testid={`input-${c.k}`} />
                </div>
                <Slider min={1} max={10} step={0.5} value={[scores[c.k]]}
                  onValueChange={(v) => setScores(s => ({ ...s, [c.k]: v[0] }))} />
              </div>
            ))}
          </div>

          <div className="mt-8">
            <label className="label-mono block mb-2">Internal Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} maxLength={2000}
              placeholder="Internal notes — not visible to teams" rows={3} className="input-field resize-none" data-testid="judge-notes" />
          </div>

          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label-mono">Weighted Total · live</div>
              <div className="font-display text-4xl text-brand mt-1" data-testid="weighted-total">{weighted.toFixed(2)}<span className="text-xl text-gray-500"> / 10</span></div>
            </div>
            <button onClick={submit} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="submit-score-btn">
              {busy ? <span className="spinner" /> : <Send className="w-4 h-4" />} Submit Score
            </button>
          </div>
        </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <div className="label-mono">{label}</div>
    <div className="mt-1">{children}</div>
  </div>
);
